import { leagueAllowanceSchema, MAX_LEAGUE_ALLOWANCE } from '@league/shared/entitlements';
import type { AccessCodeOfferResponse, MembershipOfferResponse, MetaResponse, MyMembershipResponse, PlansResponse, PlanSummary } from '@league/shared/contract';
import { formatMoney } from '@league/shared/money';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';

/**
 * THE SCREEN THAT TAKES THE MONEY, and the only one she can reach until she has paid.
 *
 * ⚠ IT WAS A CHOICE AND IS NOW A CONFIRMATION — 2026-09-15, when annual was withdrawn.
 *
 * It used to offer monthly against annual as two cards, with a computed saving badge and a
 * per-month figure under the yearly price. There is one membership now, so there is nothing to
 * choose between: presenting a single card as though it were an option would be a decision shaped
 * like a question, and a radio group with one radio in it reads as something broken.
 *
 * The rate family is still server-resolved. She now selects purchased league capacity before
 * reviewing the recurring total; the base membership is not a choice between rate families.
 *
 * ⚠ NOT ONE NUMBER IS TYPED INTO THIS FILE. Every figure comes from the `plans` table through
 * `@league/shared/money`, which is the same table and the same formatter the public page uses —
 * `PRICING.md`: "No price is a number in code." The rate family is resolved on the SERVER from her
 * membership and the request carries no part of it, so nothing here could ask for the founding
 * price even if it wanted to.
 *
 * ⚠ AND NOTHING HERE MENTIONS A FOUNDING PLACE NUMBER. She is told she is a Founding Director,
 * which is the fact that matters; she is never told she is the nineteenth.
 */

export function ChooseMembership(_props: { founding: boolean }) {
  const queryClient = useQueryClient();
  /* Open on staging only — the API says which, so this screen never holds its own copy of the rule. */
  const meta = useQuery({
    queryKey: ['meta'],
    queryFn: () => apiFetch<MetaResponse>('/api/meta'),
    staleTime: Infinity,
  });
  const signupOpen = meta.data?.directorSignupOpen === true;
  const [code, setCode] = useState('');
  const [trialCode, setTrialCode] = useState('');
  const [selection, setSelection] = useState<string | null>(null);
  const [consultationChoice, setConsultationChoice] = useState<boolean | null>(null);
  const brand = queryClient.getQueryData<{ brand: string }>(['meta'])?.brand ?? 'the platform';

  const catalogue = useQuery({
    queryKey: ['plans'],
    queryFn: () => apiFetch<PlansResponse>('/api/public/plans'),
  });

  const checkout = useMutation({
    mutationFn: () => apiPost<{ url: string }>('/api/billing/checkout', { withConsultation: paidCall, leagueAllowance: leagueAllowanceSchema.parse(selectedAllowance) }),
    onSuccess: (result) => {
      window.location.href = result.url;
    },
  });

  /*
    Whether this site takes codes at all. The real platform answers false, so the field never
    appears there — and the server refuses redemption regardless, which is the actual guard.
  */
  const accessCode = useQuery({
    queryKey: ['billing', 'access-code'],
    queryFn: () => apiFetch<AccessCodeOfferResponse>('/api/billing/access-code'),
  });

  const redeem = useMutation({
    mutationFn: () => apiPost('/api/billing/access-code', { code }),
    /* The gate above this screen re-reads her membership and lets her through. */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });

  /* The gate above this screen, or Settings, re-reads her membership and opens the app. */
  const redeemTrial = useMutation({
    mutationFn: () => apiPost('/api/billing/trial-code', { code: trialCode }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
  const mine = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => apiFetch<MyMembershipResponse>('/api/billing/me'),
  });

  const offer = useQuery({
    queryKey: ['billing', 'offer'],
    queryFn: () => apiFetch<MembershipOfferResponse>('/api/billing/offer'),
  });
  const inputValue = selection ?? String(offer.data?.selectedAllowance ?? 3);
  const selectedAllowance = Number(inputValue);
  const validQuantity = inputValue.trim() !== '' && leagueAllowanceSchema.safeParse(selectedAllowance).success;
  const extraQuantity = validQuantity ? Math.max(0, selectedAllowance - 3) : 0;
  const founding = offer.data?.planKey === 'founding_monthly';
  const includedCall = founding && offer.data?.includedPlanningCall === true;
  const plans = catalogue.data?.plans ?? [];
  const membership = plans.find((p) => p.key === (founding ? 'founding_monthly' : 'standard_monthly'));
  const consultation = plans.find((p) => p.key === 'director_consultation_30');
  const paidCall = !founding && (consultationChoice ?? offer.data?.selectedConsultation ?? false);
  const extra = plans.find((p) => p.key === 'extra_league_monthly');

  if (catalogue.isPending || offer.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  if (offer.isError || offer.data?.needsReview) return <ErrorNote>Your previous membership needs review before continuing. Please contact us.</ErrorNote>;

  const monthlyTotal = (membership?.amountCents ?? 0) + extraQuantity * (extra?.amountCents ?? 0);
  const initialSubtotal = monthlyTotal + (paidCall ? consultation?.amountCents ?? 0 : 0);
  const error =
    checkout.error instanceof ApiError
      ? checkout.error.message
      : redeem.error instanceof ApiError
        ? redeem.error.message
        : null;

  return (
    <div className="mx-auto flex w-full max-w-[42rem] flex-col gap-7 px-6 py-10">
      <header>
        {founding ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
            Founding Director
          </p>
        ) : null}
        <h1 className="mt-1 font-display text-3xl leading-tight">{founding ? 'I’m glad you’re joining us.' : 'Start your membership.'}</h1>
        <p className="mt-2 max-w-[34rem] text-[14px] leading-relaxed text-ink-2">
          {founding
            ? 'You bring the love of Mahjong and the people you’re gathering. We’ll help with the details—and you’ll have a community of fellow Directors along the way.'
            : 'Cancel any time. Nothing you have run is deleted if you do.'}
        </p>
      </header>

      {/* A code from the commissioner (2026-11-01). One to a person, so it goes once she has used one. */}
      {mine.data && !mine.data.trial ? (
        <details className="rounded-md border border-light bg-paper px-5 py-4" open={Boolean(trialCode) || undefined}>
          <summary className="cursor-pointer text-[14px]">Have a free trial code?</summary>
          <form
            className="mt-3 flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              redeemTrial.mutate();
            }}
          >
            <input
              value={trialCode}
              onChange={(event) => setTrialCode(event.target.value)}
              aria-label="Free trial code"
              autoCapitalize="characters"
              className="h-11 min-w-[14rem] flex-1 rounded-md border border-control-border bg-paper px-3 text-[14px]"
            />
            <Button type="submit" variant="secondary" disabled={!trialCode.trim() || redeemTrial.isPending}>
              {redeemTrial.isPending ? 'Checking…' : 'Start free trial'}
            </Button>
          </form>
          {redeemTrial.error ? <div className="mt-3"><ErrorNote>{redeemTrial.error instanceof ApiError ? redeemTrial.error.message : 'That did not work. Try again.'}</ErrorNote></div> : null}
        </details>
      ) : null}

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {membership ? (
        <div className="rounded-md border border-light bg-paper p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <span className="text-[15px] font-medium">{founding ? 'Founding Director membership' : membership.displayName}</span>
            <span className="tabular font-display text-[30px] leading-none">
              {formatMoney(membership.amountCents, membership.currency)}
              <span className="ml-1 font-sans text-[14px] font-normal text-ink-2">/month</span>
            </span>
          </div>

          <p className="mt-5 text-[14px]">Your membership includes:</p>
          <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-[14px] leading-relaxed text-ink-2">
            <li>Up to three leagues at a time.</li>
            <li>Planning checklists, seating tools, messages, and standings.</li>
            <li>A community of fellow Directors to share ideas and experiences.</li>
          </ul>
          <p className="mt-4 text-[14px] leading-relaxed text-ink-2">Your selected allowance covers the leagues you have in draft or in progress. Completed and archived leagues do not use that allowance.</p>
          {founding ? <>
            <h2 className="mt-6 font-display text-xl">Your founding rate stays with you.</h2>
            <p className="mt-2 text-[14px] leading-relaxed text-ink-2">Join during the founding offer and keep your {formatMoney(membership.amountCents, membership.currency)} monthly rate for as long as your membership stays continuously active. If you take a break and return later, you’ll rejoin at the membership rate available then.</p>
          </> : null}
        </div>
      ) : null}

      {includedCall || !founding ? (
        <section className="rounded-md border border-light bg-sand p-5">
          <h2 className="font-display text-xl">Your questions. My experience. Time to talk it through.</h2>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-2">Spend 30 minutes one-on-one with Courtney, {brand}’s founder and an experienced league Director.</p>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-2">Bring the questions you’ve been turning over—choosing a format, welcoming players with different experience levels, managing substitutes, or keeping people excited to return. We’ll talk through your ideas and the decisions you’re facing, drawing on what I’ve learned from running leagues myself.</p>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-2">Whether you’re planning your first league or refining one you already host, this is time focused on you and your community.</p>
          {includedCall ? <p className="mt-4 text-[14px] font-medium">Included with your founding membership{consultation ? ` · ${formatMoney(consultation.amountCents, consultation.currency)} value` : ''}</p> : consultation ? (
            <label className="mt-4 flex items-start gap-3 text-[14px] leading-relaxed">
              <input type="checkbox" checked={paidCall} disabled={checkout.isPending} onChange={event => setConsultationChoice(event.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-signal" />
              <span>Add a 30-minute one-on-one consultation — {formatMoney(consultation.amountCents, consultation.currency)}, one time.</span>
            </label>
          ) : <p className="mt-4 text-[14px]">The consultation is currently unavailable.</p>}
        </section>
      ) : null}

      <section className="rounded-md border border-light bg-paper p-5" aria-labelledby="allowance-heading">
        <h2 id="allowance-heading" className="font-display text-xl">How many leagues would you like to host?</h2>
        <p className="mt-3 text-[14px] leading-relaxed text-ink-2">One, two, or three leagues are included in your membership. Each additional league is {extra ? formatMoney(extra.amountCents, extra.currency).replace(/\.00$/, '') : 'currently unavailable'}/month.</p>
        <p id="allowance-help" className="mt-2 text-[14px] leading-relaxed text-ink-2">Choose how many leagues you’re planning to host. You’ll create and set them up after joining.</p>
        <label className="mt-4 block text-[14px] font-medium" htmlFor="league-allowance">Number of leagues</label>
        <div className="mt-2 flex items-center gap-3">
          <Button variant="secondary" aria-label="Decrease league allowance" disabled={!validQuantity || selectedAllowance <= 1 || checkout.isPending} onClick={() => setSelection(String(selectedAllowance - 1))}>−</Button>
          <input id="league-allowance" type="number" inputMode="numeric" min={1} max={MAX_LEAGUE_ALLOWANCE} step={1} required
            value={inputValue} disabled={checkout.isPending} onChange={event => setSelection(event.target.value)}
            aria-invalid={!validQuantity} aria-describedby="allowance-help allowance-minimum allowance-error"
            className="h-11 w-24 min-w-0 rounded-md border border-control-border bg-paper px-3 text-[16px]" />
          <Button variant="secondary" aria-label="Increase league allowance" disabled={!validQuantity || selectedAllowance >= MAX_LEAGUE_ALLOWANCE || checkout.isPending} onClick={() => setSelection(String(selectedAllowance + 1))}>+</Button>
        </div>
        <p id="allowance-minimum" className="mt-3 text-[13px] leading-relaxed text-ink-2">Starting with one or two? You’ll still have room for three at no additional cost.</p>
        <p id="allowance-error" role="status" className="mt-2 text-[13px] text-signal-d">{!validQuantity ? `Enter a whole number between 1 and ${MAX_LEAGUE_ALLOWANCE}.` : ''}</p>
      </section>

      <section className="rounded-md border border-light bg-paper">
        <h2 className="px-5 pt-4 font-display text-xl">Your membership summary</h2>
        <dl className="mt-3 flex flex-col divide-y divide-light">
          <Line label={`${founding ? 'Founding Director membership' : membership?.displayName ?? 'Membership'} · 3 leagues included`} value={membership} />
          {extraQuantity > 0 && extra ? <div className="flex items-baseline justify-between gap-4 px-5 py-3"><dt className="text-[14px] text-ink-2">{extraQuantity} additional {extraQuantity === 1 ? 'league' : 'leagues'} · {formatMoney(extra.amountCents, extra.currency).replace(/\.00$/, '')} each</dt><dd className="shrink-0 text-[14px]">{formatMoney(extraQuantity * extra.amountCents, extra.currency, true)}/month</dd></div> : null}
          {includedCall ? <div className="flex items-baseline justify-between gap-4 px-5 py-3"><dt className="text-[14px] text-ink-2">One-on-one consultation</dt><dd className="text-[14px]">Included</dd></div> : null}
          {paidCall && consultation ? <div className="flex items-baseline justify-between gap-4 px-5 py-3"><dt className="text-[14px] text-ink-2">One-on-one consultation</dt><dd className="shrink-0 text-[14px]">{formatMoney(consultation.amountCents, consultation.currency)} one time</dd></div> : null}
          <div className="flex items-baseline justify-between gap-4 px-5 py-4">
            <dt className="text-[14px] font-medium">{paidCall || includedCall ? 'Ongoing monthly total' : 'Monthly total'}</dt>
            <dd aria-live="polite" aria-atomic="true" className="tabular shrink-0 font-display text-[22px] leading-none">{validQuantity ? formatMoney(monthlyTotal, membership?.currency ?? 'usd') : '—'}<span className="text-[13px] font-sans">/month</span></dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 px-5 py-4">
            <dt className="text-[14px] font-medium">Initial subtotal</dt>
            <dd aria-live="polite" aria-atomic="true" className="tabular shrink-0 font-display text-[22px] leading-none">{validQuantity && (!paidCall || consultation) ? formatMoney(initialSubtotal, membership?.currency ?? 'usd') : '—'}</dd>
          </div>
        </dl>
        <p className="px-5 pb-4 text-[13px] leading-relaxed text-ink-2">Your membership renews monthly.</p>
      </section>

      {/*
        ⚠ STAGING ONLY, AND IT IS THE SERVER THAT SAYS SO.

        A handful of people are acting as directors on staging before Stripe has keys, and without
        this the only way in is somebody running a command on the box for each of them.

        Last on the screen and quiet on purpose: it is a side door for a few people, not an
        alternative to paying. On the real platform the server answers that codes are not offered
        and none of this renders — a "have an access code?" box in front of a paying director is an
        invitation to go and look for one.
      */}
      {accessCode.data?.offered ? (
        <details className="rounded-md border border-light bg-paper px-5 py-4">
          <summary className="cursor-pointer text-[13px] text-ink-2">
            Testing this site? Enter an access code instead.
          </summary>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-label="Access code"
              placeholder="Access code"
              className="h-11 min-w-[14rem] flex-1 rounded-md border border-control-border bg-paper px-3 text-[14px]"
            />
            <Button
              variant="secondary"
              disabled={!code.trim() || redeem.isPending}
              onClick={() => redeem.mutate()}
            >
              {redeem.isPending ? 'Checking…' : 'Use code'}
            </Button>
          </div>
          <p className="mt-2 text-[13px] text-ink-2">
            No card is taken and nothing is charged. Your account is marked as complimentary.
          </p>
        </details>
      ) : null}

      {/*
        PAYMENTS OPEN SOON — `DIRECTOR_SIGNUP_OPEN`, owner 2026-09-27. The server refuses checkout
        while it is off, so the button is replaced rather than left to fail when pressed.
      */}
      {signupOpen ? (
        <div>
          <Button disabled={checkout.isPending || !membership || !validQuantity || (extraQuantity > 0 && !extra) || (paidCall && !consultation)} onClick={() => checkout.mutate()}>
            {checkout.isPending ? 'Opening…' : 'Continue to payment'}
          </Button>
          <p className="mt-2.5 text-[13px] text-ink-2">
            Review your selection and payment details on the next screen.
          </p>
        </div>
      ) : (
        <div role="status" className="rounded-md border border-dashed border-control-border bg-paper px-5 py-4">
          <p className="text-[15px] font-semibold">Payments open soon.</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
            We’re finishing the payment setup for Director memberships. Your account is saved — come back
            here to start your membership as soon as it’s ready.
          </p>
        </div>
      )}
    </div>
  );
}

function Line({ label, value }: { label: string; value: PlanSummary | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3">
      <dt className="text-[14px] text-ink-2">{label}</dt>
      <dd className="tabular text-[14px]">{formatMoney(value.amountCents, value.currency)}/month</dd>
    </div>
  );
}
